<!-- 产品详情页（新版） -->
<template>
  <div class="product-detail-new">
    <!-- ==================== 详情页 ==================== -->
    <div v-if="pageMode === 'detail'" class="detail-page">
      <!-- 顶部导航 -->
      <div class="detail-header">
        <div class="header-back" @click="handleBack">
          <van-icon name="arrow-left" size="22px" />
        </div>
        <div class="header-title">{{ productInfo.productName }}</div>
        <div class="header-placeholder" />
      </div>

      <div class="detail-body">
        <!-- 额度信息区 -->
        <div class="quota-section">
          <div class="quota-range">
            额度可借范围：{{ productInfo.minAmount }}-{{ productInfo.maxAmount }}元
          </div>

          <!-- 今日名额标签 -->
          <div class="quota-badge">今日名额仅剩{{ getRandomQuota() }}%</div>

          <!-- 额度金额 -->
          <div class="quota-amount">
            <div class="amount-display">
              <span class="amount-prefix">最高可借</span>
              <span class="amount-value">{{ productInfo.maxAmount }}</span>
              <span class="amount-unit">元</span>
            </div>
          </div>
        </div>

        <!-- 期限与利率 -->
        <div class="term-rate-row">
          <div class="term-card">
            <div class="term-value">{{ productInfo.loanTerm }}</div>
            <div class="term-label">期限（月）</div>
          </div>
          <div class="rate-card">
            <div class="rate-value">{{ productInfo.annualRate }}</div>
            <div class="rate-label">综合年利率</div>
          </div>
        </div>

        <!-- 审核说明 -->
        <div class="info-card">
          <div class="info-card-title">审核说明</div>
          <div class="info-row">
            <span class="info-label">审核年龄</span>
            <span class="info-value">{{ productInfo.auditAge }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">审核类型</span>
            <span class="info-value">{{ productInfo.auditType }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">手机时长</span>
            <span class="info-value">{{ productInfo.phoneDuration }}</span>
          </div>
        </div>

        <!-- 机构详情 -->
        <div class="info-card">
          <div class="info-card-title">机构详情</div>
          <div class="info-row">
            <span class="info-label">服务机构</span>
            <span class="info-value">{{ productInfo.institutionName }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">机构电话</span>
            <span class="info-value">{{ productInfo.institutionPhone }}</span>
          </div>
        </div>

        <!-- 协议勾选 -->
        <div class="protocol-row" v-if="validContracts.length">
          <van-checkbox
            v-model="agreed"
            checked-color="#ff6b35"
            icon-size="18px"
            @change="onAgreedChange"
          >
            我已阅读并同意
            <span v-for="(contract, index) in validContracts" :key="'contract-' + index">
              <template v-if="index > 0">和</template>
              <span class="protocol-link" @click.stop="onContractClick(contract)"
                >《{{ contract.contractName }}》</span
              >
            </span>
          </van-checkbox>
        </div>

        <!-- 底部占位，避免内容被固定底栏遮挡 -->
        <div class="bottom-spacer" aria-hidden="true" />
      </div>

      <!-- 协议弹窗（参考 result_zk_suc_comn.vue 样式） -->
      <van-action-sheet
        v-model="protocolShow"
        class="protocol-sheet protocol-sheet-detail"
        round
        close-on-popstate
        @close="handleCloseProtocol"
      >
        <div slot="default" class="protocol-sheet-body">
          <header class="protocol-sheet-header">
            <div v-if="protocolTitle" class="protocol-sheet-header-title">
              {{ protocolTitle }}
            </div>
            <van-icon
              name="cross"
              class="protocol-sheet-header-close"
              @click="protocolShow = false"
            />
          </header>
          <div class="protocol-sheet-scroll">
            <div class="protocol-sheet-html" v-html="protocolContent" />
          </div>
        </div>
      </van-action-sheet>
    </div>

    <!-- 底部固定操作区（放在 detail-page 外，避免被容器裁剪） -->
    <div v-if="pageMode === 'detail'" class="bottom-area">
      <van-button
        type="primary"
        block
        round
        class="apply-btn"
        :disabled="validContracts.length > 0 && !agreed"
        @click="handleApply"
      >
        立即申请
      </van-button>
      <div class="bottom-tip">
        本次借款服务由{{ productInfo.productName }}提供<br />本平台提供信息、平台不承担任何放款
      </div>
    </div>

    <!-- ==================== 匹配中页面 ==================== -->
    <div v-else-if="pageMode === 'matching'" class="matching-page">
      <div class="matching-header">
        <div class="header-close" @click="handleCloseMatching">
          <van-icon name="cross" size="22px" />
        </div>
        <div class="header-title">申请借款</div>
        <div class="header-placeholder" />
      </div>

      <div class="matching-content">
        <!-- 绿色盾牌动画 -->
        <div class="shield-wrapper">
          <div class="shield-circle">
            <div class="shield-inner">
              <van-icon name="shield-check" class="shield-icon" />
            </div>
          </div>
          <!-- 进度环 -->
          <svg class="progress-ring" width="140" height="140">
            <circle class="progress-bg" cx="70" cy="70" r="62" />
            <circle
              class="progress-bar"
              cx="70"
              cy="70"
              r="62"
              :stroke-dasharray="circumference"
              :stroke-dashoffset="progressOffset"
            />
          </svg>
        </div>

        <div class="matching-text">
          <div class="matching-title">正在为您匹配产品方</div>
          <div class="matching-subtitle">
            请耐心等待 <span class="countdown-text">{{ countdown }}秒</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as API_Common from '@/api/common.js'
import { setCookie } from '@/utils/cookies.js'
import { formatAppFrameLinkUrl } from '@/utils/appFrameUrl.js'
import { throttle } from '@/utils/function.js'

export default {
  name: 'ProductDetailNew',
  data() {
    return {
      applyId: this.$route.query.applyId,
      productId: null,
      showPage: 27, // 后端给的
      pageMode: 'detail', // 'detail' | 'matching'

      productInfo: {
        productName: '',
        minAmount: '',
        maxAmount: '',
        remainingQuota: null,
        loanTerm: '',
        annualRate: '',
        auditAge: '',
        auditType: '',
        phoneDuration: '',
        institutionName: '',
        institutionPhone: '',
      },
      productContractDTOS: [],
      checkMobileStatus: null,
      linkUrl: '',

      amountInput: '0',
      isEditingAmount: false, // 是否正在编辑额度
      agreed: false,

      // 协议弹窗
      protocolShow: false,
      protocolTitle: '',
      protocolContent: '',

      // 匹配中相关
      countdown: 3,
      countdownTimer: null,
      matchingDelayTimer: null,
      matchingStartTime: 0,
      matchingMinMs: 1000,
      circumference: 2 * Math.PI * 62,
      progressPercent: 0,
    }
  },
  computed: {
    progressOffset() {
      return this.circumference - (this.progressPercent / 100) * this.circumference
    },
    validContracts() {
      return (this.productContractDTOS || []).filter((c) => c && c.contractName)
    },
  },
  created() {
    window.__TRACK_SCENE_KEY__ = 'productDetailNew'
  },

  mounted() {
    if (this.$route.query.showPage) {
      this.showPage = Number(this.$route.query.showPage)
    }

    // 推一个历史状态，拦截浏览器返回键
    window.history.pushState(null, null, window.location.href)
    window.addEventListener('popstate', this.handlePopState)

    this.loadProductData()
  },

  beforeDestroy() {
    this.clearCountdown()
    this.clearMatchingDelay()
    window.removeEventListener('popstate', this.handlePopState)
    window.__TRACK_SCENE_KEY__ = ''
  },
  methods: {
    showDetail() {
      this.pageMode = 'detail'
      API_Common.handleBatchTrackBase(100060)
    },

    onAgreedChange(checked) {
      if (checked) API_Common.handleBatchTrackBase(100062)
    },

    // 返回按钮（也走 matching → 接口 → detail 的逻辑）
    handleBack() {
      this.loadProductData()
    },

    // 浏览器返回事件处理（拦截返回 + 重新触发加载流程）
    handlePopState() {
      // 再次推一个状态，阻止用户真的离开页面
      window.history.pushState(null, null, window.location.href)

      // 触发 loading → 接口 → detail 的流程
      this.loadProductData()
    },

    clearMatchingDelay() {
      if (this.matchingDelayTimer) {
        clearTimeout(this.matchingDelayTimer)
        this.matchingDelayTimer = null
      }
    },

    /** matching 模块至少展示 matchingMinMs 后再执行回调 */
    afterMatchingMinDuration(callback) {
      const remain = this.matchingMinMs - (Date.now() - this.matchingStartTime)
      if (remain <= 0) {
        callback()
        return
      }
      this.clearMatchingDelay()
      this.matchingDelayTimer = setTimeout(() => {
        this.matchingDelayTimer = null
        callback()
      }, remain)
    },

    // 加载产品数据
    async loadProductData() {
      this.clearMatchingDelay()
      this.pageMode = 'matching'
      this.matchingStartTime = Date.now()
      API_Common.handleBatchTrackBase(100059)

      if (!this.applyId) {
        this.$toast.fail('缺少申请信息')
        this.afterMatchingMinDuration(() => this.showDetail())
        return
      }

      try {
        let res = await API_Common.pushCpaProductDetail(Number(this.applyId))

        // 匹配到产品
        if (res.code === 200 && res.data && res.data.productId) {
          this.applyProductDetail(res.data)
          this.afterMatchingMinDuration(() => this.showDetail())
        } else {
          // 未匹配到产品
          this.afterMatchingMinDuration(() => {
            this.$router.push({
              path: '/results_empty',
            })
          })
        }
      } catch (e) {
        console.error('pushCpaProductDetail error:', e)
        this.$toast.fail('获取产品信息失败')
        this.afterMatchingMinDuration(() => this.showDetail())
      }
    },

    /** 参考 index.vue：将 pushCpaProductDetail 返回写入页面 */
    applyProductDetail(data) {
      if (!data) return

      const audit = this.parseAuditInfo(data.auditInfo)

      this.productId = data.productId
      this.checkMobileStatus = data.checkMobileStatus
      this.linkUrl = data.linkUrl || ''
      this.productContractDTOS = data.productContractDTOS || []

      this.productInfo = {
        productName: data.productName || '',
        minAmount: data.minAmount || '',
        maxAmount: data.maxAmount || '',
        remainingQuota: data.remainingQuota != null ? data.remainingQuota : null,
        loanTerm:
          data.loanMinPeriod != null && data.loanPeriod != null
            ? `${data.loanMinPeriod}-${data.loanPeriod}`
            : data.loanPeriod || '',
        annualRate: this.formatAnnualRate(data),
        auditAge: audit.age || '—',
        auditType: audit.auditType || '—',
        phoneDuration: audit.phoneDuration || '—',
        institutionName: data.organizationName || '',
        institutionPhone: data.organizationPhone || '',
      }

      const credit = this.parseCreditAmount(data.creditAmount, data.maxAmount)
      this.amountInput = String(credit)
    },

    parseAuditInfo(auditInfo) {
      const empty = { age: '', auditType: '', phoneDuration: '' }
      if (!auditInfo || auditInfo === '') return empty
      try {
        const obj = typeof auditInfo === 'string' ? JSON.parse(auditInfo) : auditInfo
        const phone = (obj['手机时长'] || '').trim()
        return {
          age: obj['年龄'] || '',
          auditType: obj['审核类型'] || '',
          phoneDuration: phone,
        }
      } catch (e) {
        console.error('解析 auditInfo 失败:', e)
        return empty
      }
    },

    formatAnnualRate(data) {
      // 产品详情与贷超列表统一：有最大值用接口值，缺失时显示 24%。
      const min = data.minLoanRate || data.timeLimitLoanRate
      if (min) return `${min}-${data.maxLoanRate || 24}%`
      // 缺少下限时仍显示产品最大利率，不把已有上限隐藏掉。
      return `${data.maxLoanRate || 24}%`
    },

    parseCreditAmount(creditAmount, maxAmount) {
      const toNum = (v) => {
        if (v == null || v === '') return NaN
        return parseInt(String(v).replace(/,/g, ''), 10)
      }
      let n = toNum(creditAmount)
      if (Number.isNaN(n)) n = toNum(maxAmount)
      return Number.isNaN(n) ? 0 : n
    },

    onContractClick(contract) {
      if (!contract || !contract.contractName) return
      this.protocolTitle = contract.contractName
      if (contract.contractContent) {
        this.protocolContent = contract.contractContent
      } else if (contract.contractUrl) {
        const url = String(contract.contractUrl).trim()
        this.protocolContent = `<iframe src="${url}" style="width:100%;min-height:60vh;border:none"></iframe>`
      } else {
        this.protocolContent = '<p>暂无协议内容</p>'
      }
      this.protocolShow = true
    },
    // 关闭协议弹窗
    handleCloseProtocol() {
      this.protocolTitle = ''
      this.protocolContent = ''
    },
    // 立即申请（与 index.vue handleApply 一致）
    handleApply: throttle(async function () {
      API_Common.handleBatchTrackBase(100061)
      this.getProductCheckUrl()
    }),

    // 立即申请，请求跳转 url（与 index.vue 一致）
    getProductCheckUrl() {
      API_Common.buried({ productId: this.productId, showPage: this.showPage })
        .then((res) => {
          this.productId = res.data.productId || this.productId
          setCookie('linkUrl', res.data.linkUrl)
          this.jumpAppFrameAfterLoanRecord(res.data.linkUrl)
        })
        .catch((err) => {
          console.error('getProductCheckUrl error:', err)
          this.$toast.fail('获取产品信息失败')
        })
    },
    // 清除倒计时（进入页 matching 加载用）
    clearCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },

    // 关闭匹配页
    handleCloseMatching() {
      this.clearCountdown()
      this.clearMatchingDelay()
      this.pageMode = 'detail'
      this.progressPercent = 0
    },

    getRandomQuota() {
      // 随机生成一个10-40之间的整数
      return Math.floor(Math.random() * 37) + 10
    },
    jumpAppFrameAfterLoanRecord(linkUrl) {
      window.location.href =
        '/appFrame?productId=' + this.productId + '&url=' + formatAppFrameLinkUrl(linkUrl)
      sessionStorage.setItem('lastUrl', window.location.href)
    },
  },
}
</script>

<style lang="scss" scoped>
.product-detail-new {
  min-height: 100vh;
  min-height: 100dvh;
  background: #f7f8fa;
}

.detail-page {
  min-height: 100vh;
  min-height: 100dvh;
  box-sizing: border-box;
}

.detail-body {
  box-sizing: border-box;
}

.bottom-spacer {
  height: calc(2.4rem + env(safe-area-inset-bottom));
}

/* 详情页顶部 */
.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 0.88rem;
  background: #fff;
  padding: 0 0.16rem;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-back {
  width: 0.44rem;
  height: 0.44rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
}

.header-title {
  font-size: 0.32rem;
  font-weight: 600;
  color: #222;
}

.header-placeholder {
  width: 0.44rem;
}

/* 额度区（使用品牌背景图） */
.quota-section {
  background-image: url('../../assets/images/image_orange/result/top_bg.png');
  background-size: cover;
  background-position: top center;
  background-repeat: no-repeat;
  padding: 1.4rem 0.32rem 1.2rem;
  position: relative;
}

.quota-range {
  font-size: 0.24rem;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 0.16rem;
}

/* 今日名额标签（高对比突出版） */
.quota-badge {
  position: absolute;
  top: 0.28rem;
  right: 0.24rem;
  background: #fff;
  color: #ff6b35;
  border-radius: 0.32rem;
  padding: 0.08rem 0.18rem;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.2);
  font-size: 0.2rem;
  height: 0.64rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 107, 53, 0.2);
}

/* 额度金额区域 */
.quota-amount {
  display: flex;
  align-items: center;
  margin-top: 0.12rem;
  min-height: 0.8rem;
}

/* 显示模式：大数字 + 编辑图标 */
.amount-display {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.amount-value {
  font-size: 0.72rem;
  font-weight: 700;
  color: #fff;
  line-height: 1;
}

/* “最高可借” 前缀（参考图大字样式） */
.amount-prefix {
  font-size: 0.52rem;
  color: rgba(255, 255, 255, 0.9);
  margin-right: 0.16rem;
  font-weight: 600;
}

.amount-unit {
  font-size: 0.32rem;
  color: rgba(255, 255, 255, 0.85);
  margin-left: 0.08rem;
  margin-right: 0.12rem;
}

/* 小圆形编辑图标（参考图样式） */
.edit-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 0.36rem;
  height: 0.36rem;
  background: #f0f0f0;
  border-radius: 50%;
  margin-left: 0.08rem;
}

.edit-icon {
  font-size: 0.2rem;
  color: #999;
}

/* 编辑模式输入框 */
.amount-edit {
  display: flex;
  align-items: center;
}

.amount-input {
  width: 3.2rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: #222;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  outline: none;
  text-align: left;
  border-bottom: 2px solid #ff6b35;
}

.amount-input:focus {
  border-bottom-color: #ff6b35;
}

/* 清除按钮 */
.clear-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 0.36rem;
  height: 0.36rem;
  margin-left: 0.08rem;
  margin-right: 0.08rem;
  background: #ccc;
  color: #fff;
  font-size: 0.28rem;
  border-radius: 50%;
  cursor: pointer;
  user-select: none;
  line-height: 1;
}

.clear-btn:active {
  background: #999;
}

/* 期限利率行 */
.term-rate-row {
  display: flex;
  gap: 0.16rem;
  padding: 0.2rem 0.24rem;
  background: #f7f8fa;
}

.term-card,
.rate-card {
  flex: 1;
  background: #fff;
  border-radius: 0.12rem;
  padding: 0.2rem 0.24rem;
  text-align: center;
}

.term-value,
.rate-value {
  font-size: 0.36rem;
  font-weight: 600;
  color: #222;
}

.term-label,
.rate-label {
  font-size: 0.22rem;
  color: #888;
  margin-top: 0.08rem;
}

/* 信息卡片 */
.info-card {
  background: linear-gradient(
    180deg,
    rgba(255, 107, 53, 0.12) 0%,
    rgba(255, 107, 53, 0.06) 40%,
    rgba(255, 255, 255, 0) 100%
  );
  margin: 0.16rem 0.24rem;
  border-radius: 0.12rem;
  padding: 0.2rem 0.24rem;
  border: 1px solid rgba(255, 107, 53, 0.08);
}

.info-card-title {
  font-size: 0.26rem;
  font-weight: 600;
  color: #222;
  margin-bottom: 0.16rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 0.1rem 0;
  font-size: 0.26rem;
}

.info-label {
  color: #666;
}

.info-value {
  color: #222;
}

/* 协议行 */
.protocol-row {
  padding: 0.2rem 0.24rem;
  font-size: 0.24rem;
  color: #666;
}

.protocol-link {
  color: #ff6b35;
}

/* 底部固定操作区 */
.bottom-area {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  background: #fff;
  padding: 0.2rem 0.24rem calc(0.2rem + env(safe-area-inset-bottom));
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
}

.apply-btn {
  height: 0.88rem;
  font-size: 0.32rem;
  font-weight: 600;
  background: linear-gradient(135deg, #ff6b35 0%, #ff8a5b 100%);
  border: none;
}

.apply-btn:disabled {
  background: #ddd;
  color: #999;
}

.bottom-tip {
  margin-top: 0.16rem;
  font-size: 0.2rem;
  color: #999;
  text-align: center;
  line-height: 1.4;
}

/* ==================== 匹配中页面 ==================== */
.matching-page {
  min-height: 100vh;
  background: #fff;
}

.matching-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 0.88rem;
  padding: 0 0.16rem;
  border-bottom: 1px solid #f0f0f0;
}

.header-close {
  width: 0.44rem;
  height: 0.44rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.matching-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 2rem;
}

.shield-wrapper {
  position: relative;
  width: 140px;
  height: 140px;
  margin-bottom: 0.6rem;
}

.shield-circle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #c6f0d1 0%, #9be8a8 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.shield-inner {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #7ed98f 0%, #4caf50 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.shield-icon {
  font-size: 40px;
  color: #fff;
}

.progress-ring {
  position: absolute;
  top: 0;
  left: 0;
  transform: rotate(-90deg);
  z-index: 1;
}

.progress-bg {
  fill: none;
  stroke: #e8f5e9;
  stroke-width: 8;
}

.progress-bar {
  fill: none;
  stroke: #4caf50;
  stroke-width: 8;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.3s ease;
}

.matching-text {
  text-align: center;
}

.matching-title {
  font-size: 0.32rem;
  color: #333;
  font-weight: 500;
  margin-bottom: 0.16rem;
}

.matching-subtitle {
  font-size: 0.26rem;
  color: #888;
}

.countdown-text {
  color: #4caf50;
  font-weight: 600;
}

/* ==================== 协议弹窗样式（参考 result_zk_suc_comn.vue） ==================== */
.protocol-sheet-detail.van-action-sheet {
  max-height: 70vh;
}

.protocol-sheet-detail .van-action-sheet__content {
  padding: 0;
  max-height: inherit;
  background: #fff;
}

.protocol-sheet-body {
  display: flex;
  flex-direction: column;
  max-height: 70vh;
  overflow: hidden;
}

.protocol-sheet-header {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0.88rem;
  padding: 0.2rem 0.88rem;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
  border-bottom: 1px solid #eef0f3;
}

.protocol-sheet-header-title {
  max-width: 100%;
  padding: 0 0.08rem;
  font-size: 0.32rem;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.35;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.protocol-sheet-header-close {
  position: absolute;
  right: 0.24rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.4rem;
  color: #b0b6bf;
  padding: 0.12rem;
  line-height: 1;
  cursor: pointer;
}

.protocol-sheet-scroll {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0.28rem 0.24rem;
}

.protocol-sheet-html {
  font-size: 0.26rem;
  color: #555;
  line-height: 1.7;
  padding-top: 0.12rem;
}

.protocol-sheet-html p {
  margin-bottom: 0.24rem;
}
</style>
